import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';

interface ViewerSensitiveQueryLike {
  readonly queryKey?: readonly unknown[] | undefined;
  readonly meta?:
    | {
        readonly specName?: unknown;
        readonly viewerSensitive?: unknown;
        readonly persist?: unknown;
      }
    | undefined;
}

const viewerSensitivePrefixes = new Set<string>();
const viewerSensitiveSpecNames = new Set<string>();

export interface ViewerSensitiveRecoveryTarget {
  readonly queryKey: QueryKey;
  readonly queryHash: string;
  readonly specName: string;
}

export function registerViewerSensitiveSpec(spec: {
  readonly kind: string;
  readonly specName: string;
  readonly viewerSensitive?: boolean;
  readonly entityKeys: Record<string, unknown>;
}): void {
  if (spec.viewerSensitive !== true) return;
  viewerSensitiveSpecNames.add(spec.specName);

  const factory = spec.entityKeys[spec.kind] as
    | ((args: Record<string, never>) => { readonly queryKey: readonly unknown[] })
    | undefined;
  if (typeof factory !== 'function') return;
  try {
    const prefix = staticPrefixOf(factory({}).queryKey);
    if (prefix.length > 0) viewerSensitivePrefixes.add(prefix);
  } catch {
    // Observed queries retain the metadata fast-path even when a factory
    // cannot build a prefix without concrete route arguments.
  }
}

export function isViewerSensitiveQuery(query: ViewerSensitiveQueryLike): boolean {
  if (query.meta?.viewerSensitive === true || query.meta?.persist === false) return true;
  if (
    typeof query.meta?.specName === 'string' &&
    viewerSensitiveSpecNames.has(query.meta.specName)
  ) {
    return true;
  }
  if (!Array.isArray(query.queryKey)) return false;
  const key = staticPrefixOf(query.queryKey);
  for (const prefix of viewerSensitivePrefixes) {
    if (key === prefix || key.startsWith(`${prefix}\0`)) return true;
  }
  return false;
}

export function getActiveViewerSensitiveQueries(
  queryClient: QueryClient,
): readonly ViewerSensitiveRecoveryTarget[] {
  return queryClient
    .getQueryCache()
    .getAll()
    .filter((query) => query.observers.length > 0 && isViewerSensitiveQuery(query))
    .map((query) => ({
      queryKey: query.queryKey,
      queryHash: hashDiagnosticEntity(query.queryHash),
      specName: typeof query.meta?.specName === 'string' ? query.meta.specName : 'unknown-spec',
    }));
}

export async function purgeViewerSensitiveQueries(queryClient: QueryClient): Promise<void> {
  const filters = { predicate: isViewerSensitiveQuery as (query: Query) => boolean };
  const sensitiveQueries = queryClient.getQueryCache().getAll().filter(isViewerSensitiveQuery);
  const cancellation = queryClient.cancelQueries(filters, { revert: false });

  for (const query of sensitiveQueries) {
    if (query.observers.length === 0) {
      queryClient.getQueryCache().remove(query);
      continue;
    }
    clearViewerData(query);
  }
  await cancellation;
  // A cancellation callback may settle after the synchronous privacy clear.
  // Re-assert the empty state so recovery always starts from a clean boundary.
  for (const query of sensitiveQueries) {
    if (query.observers.length > 0) clearViewerData(query);
  }
}

export async function invalidateViewerSensitiveQueries(
  queryClient: QueryClient,
  refetchType: 'active' | 'inactive' | 'all' | 'none' = 'active',
): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: isViewerSensitiveQuery as (query: Query) => boolean,
    refetchType,
  });
}

export function getViewerSensitiveSpecNames(): ReadonlySet<string> {
  return viewerSensitiveSpecNames;
}

export function excludeViewerSensitiveQueriesFromPersistedClient<T>(client: T): T {
  if (!isRecord(client) || !isRecord(client.clientState)) return client;
  const queries = client.clientState.queries;
  if (!Array.isArray(queries)) return client;
  const eligibleQueries = queries.filter(
    (query) => !isViewerSensitiveQuery(isRecord(query) ? query : {}),
  );
  if (eligibleQueries.length === queries.length) return client;
  return {
    ...client,
    clientState: { ...client.clientState, queries: eligibleQueries },
  } as T;
}

export function resetViewerSensitiveQueryRegistryForTests(): void {
  viewerSensitivePrefixes.clear();
  viewerSensitiveSpecNames.clear();
}

function staticPrefixOf(queryKey: readonly unknown[]): string {
  const segments: string[] = [];
  for (const segment of queryKey) {
    if (typeof segment !== 'string') break;
    segments.push(segment);
  }
  return segments.join('\0');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function clearViewerData(query: Query): void {
  query.setState({
    data: undefined,
    dataUpdatedAt: 0,
    error: null,
    errorUpdatedAt: 0,
    fetchFailureCount: 0,
    fetchFailureReason: null,
    fetchStatus: 'idle',
    isInvalidated: true,
    status: 'pending',
  });
}
